import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@src/lib/utils/cn";

const RANGE_THUMB_NAMES = ["minimum", "maximum"];

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(
  (
    {
      className,
      // Named on the focusable thumb, not the role-less root, so screen
      // readers announce it.
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    // One thumb per value, so a two-value range gets both handles.
    const thumbCount = Math.max(
      1,
      (props.value ?? props.defaultValue ?? []).length,
    );
    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
          className,
        )}
        {...props}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-border-tertiary">
          <SliderPrimitive.Range className="absolute h-full bg-action-primary" />
        </SliderPrimitive.Track>
        {Array.from({ length: thumbCount }, (_, index) => (
          <SliderPrimitive.Thumb
            key={index}
            // A range's handles need distinct names: "<label> minimum" /
            // "<label> maximum" (Radix's own fallback when unnamed).
            aria-label={
              thumbCount > 1
                ? [ariaLabel, RANGE_THUMB_NAMES[index] ?? `value ${index + 1}`]
                    .filter(Boolean)
                    .join(" ")
                : ariaLabel
            }
            aria-labelledby={thumbCount > 1 ? undefined : ariaLabelledBy}
            className="block size-4 cursor-grab rounded-full border-2 border-action-primary bg-action-primary-foreground shadow-default transition-[box-shadow] outline-none hover:ring-4 hover:ring-brand-bg focus-visible:ring-[3px] focus-visible:ring-brand-border active:cursor-grabbing data-[disabled]:cursor-not-allowed data-[disabled]:hover:ring-0"
          />
        ))}
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
